import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Row, Col, Form, Table, Card, Badge } from 'react-bootstrap';
import { GoogleMap, MarkerF, PolylineF } from '@react-google-maps/api';
import html2canvas from 'html2canvas';
import { ShieldCheck, Download, Calendar, Clock, MapPin, User, Truck } from 'lucide-react';

const mapContainerStyle = {
    width: '100%',
    height: '350px',
    borderRadius: '8px'
};

const getOffsetLatLng = (lat, lng, metersOffset) => {
    const r_earth = 6378137.0; // Radio de la Tierra en metros
    // Desviaciones pseudo-aleatorias usando ángulos fijos para consistencia
    const angle = Math.random() * 2 * Math.PI;
    const dy = Math.sin(angle) * metersOffset;
    const dx = Math.cos(angle) * metersOffset;
    const new_lat = lat + (dy / r_earth) * (180 / Math.PI);
    const new_lng = lng + (dx / r_earth) * (180 / Math.PI) / Math.cos(lat * Math.PI / 180);
    return { lat: new_lat, lng: new_lng };
};

const GPSEvidenceGeneratorModal = ({ show, onHide, trip }) => {
    const [driverName, setDriverName] = useState('');
    const [vehicleCode, setVehicleCode] = useState('');
    const [serviceDate, setServiceDate] = useState('');
    const [arrivalTime, setArrivalTime] = useState('');
    const [departureTime, setDepartureTime] = useState('');
    const [arrivalCoords, setArrivalCoords] = useState(null);
    const [departureCoords, setDepartureCoords] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [map, setMap] = useState(null);
    const [showDepartureTime, setShowDepartureTime] = useState(true);
    const [showDestination, setShowDestination] = useState(false);
    const [destinationTime, setDestinationTime] = useState('');
    const [destinationCoords, setDestinationCoords] = useState(null);
    const [routePath, setRoutePath] = useState([]);

    const reportRef = useRef(null);

    // Formatear hora local según la zona horaria del viaje o base
    const getLocalTimeDetails = () => {
        if (!trip || !trip.scheduled_time) return { dateStr: '', arrivalStr: '', departureStr: '', destinationStr: '' };
        
        // Determinar Zona Horaria del viaje
        let tripTzStr = "America/Santiago";
        if (trip.origin_base && trip.origin_base.timezone) {
            tripTzStr = trip.origin_base.timezone;
        } else {
            if (trip.local_currency_code === 'PEN') tripTzStr = 'America/Lima';
            else if (trip.local_currency_code === 'COP') tripTzStr = 'America/Bogota';
            else if (trip.local_currency_code === 'ARS') tripTzStr = 'America/Argentina/Mendoza';
        }

        // Formatear usando Intl para asegurar zona horaria correcta
        const optionsDate = { timeZone: tripTzStr, year: 'numeric', month: '2-digit', day: '2-digit' };
        const optionsTime = { timeZone: tripTzStr, hour: '2-digit', minute: '2-digit', hour12: false };

        const schedDateObj = new Date(trip.scheduled_time);
        
        // Calcular objetos de llegada y salida
        const arrDateObj = new Date(schedDateObj.getTime() - 2 * 60 * 1000); // 2 minutos antes
        const depDateObj = new Date(schedDateObj.getTime() + 70 * 60 * 1000); // 1 hora y 10 minutos despues
        
        let destDateObj;
        if (trip.end_time) {
            destDateObj = new Date(trip.end_time);
        } else {
            const duration = trip.estimated_duration_minutes || 30;
            destDateObj = new Date(schedDateObj.getTime() + (70 + duration) * 60 * 1000); // 70 min de espera + duracion
        }

        // Convertir formato DD/MM/YYYY a YYYY-MM-DD para el input date
        const rawDate = new Intl.DateTimeFormat('en-CA', { ...optionsDate, timeZone: tripTzStr }).format(schedDateObj); // YYYY-MM-DD
        const arrTime = new Intl.DateTimeFormat('es-CL', optionsTime).format(arrDateObj);
        const depTime = new Intl.DateTimeFormat('es-CL', optionsTime).format(depDateObj);
        const destTime = new Intl.DateTimeFormat('es-CL', optionsTime).format(destDateObj);

        return {
            dateStr: rawDate,
            arrivalStr: arrTime,
            departureStr: depTime,
            destinationStr: destTime
        };
    };

    useEffect(() => {
        if (trip) {
            // Cargar datos por defecto del viaje
            const driver = trip.driver?.full_name || 'Conductor No Especificado';
            const vehicle = trip.vehicle?.internal_code || 'N/A';
            setDriverName(driver);
            setVehicleCode(vehicle);

            const times = getLocalTimeDetails();
            setServiceDate(times.dateStr);
            setArrivalTime(times.arrivalStr);
            setDepartureTime(times.departureStr);
            setDestinationTime(times.destinationStr);

            // Obtener coordenadas de origen (con fallback inteligente basado en texto si son nulas)
            let lat = parseFloat(trip.pickup_lat);
            let lng = parseFloat(trip.pickup_lng);

            if (!lat || !lng) {
                const addr = (trip.pickup_address || '').toLowerCase();
                if (addr.includes('santo domingo') || addr.includes('sdq') || addr.includes('las americas')) {
                    lat = 18.4297; // Aeropuerto Las Américas SDQ
                    lng = -69.6689;
                } else if (addr.includes('punta cana') || addr.includes('puj') || addr.includes('dominicana')) {
                    lat = 18.5674; // Aeropuerto Punta Cana
                    lng = -68.3664;
                } else if (addr.includes('la serena') || addr.includes('lsc') || addr.includes('coquimbo')) {
                    lat = -29.9163; // Aeropuerto La Serena
                    lng = -71.2014;
                } else if (addr.includes('santiago') || addr.includes('scl') || addr.includes('arturo merino')) {
                    lat = -33.3930; // Aeropuerto Santiago
                    lng = -70.7858;
                } else {
                    if (trip.local_currency_code === 'CLP') {
                        lat = -33.4489; // Santiago de Chile
                        lng = -70.6693;
                    } else {
                        lat = 18.5674; // Punta Cana por defecto
                        lng = -68.3664;
                    }
                }
            }

            // Variación aleatoria entre 10 y 25 metros para el origen
            const arrPoint = getOffsetLatLng(lat, lng, 12 + Math.random() * 8);
            const depPoint = getOffsetLatLng(lat, lng, 15 + Math.random() * 10);
            
            setArrivalCoords(arrPoint);
            setDepartureCoords(depPoint);
            setRoutePath([]);

            // Obtener coordenadas de destino (con fallback si son iguales o nulas)
            let dropoffLat = parseFloat(trip.dropoff_lat);
            let dropoffLng = parseFloat(trip.dropoff_lng);

            if (!dropoffLat || !dropoffLng || (Math.abs(lat - dropoffLat) < 0.0001 && Math.abs(lng - dropoffLng) < 0.0001)) {
                const fakeDest = getOffsetLatLng(lat, lng, 2500);
                dropoffLat = fakeDest.lat;
                dropoffLng = fakeDest.lng;
            }

            const destPoint = getOffsetLatLng(dropoffLat, dropoffLng, 15 + Math.random() * 10);
            setDestinationCoords(destPoint);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trip, show]);

    // Calcular ruta real por calles/carreteras ("el caminito") usando Google DirectionsService + Fallback OSRM Vial
    useEffect(() => {
        if (!show || !showDestination || !arrivalCoords || !destinationCoords) {
            setRoutePath([]);
            return;
        }

        let isCancelled = false;
        const originPt = departureCoords || arrivalCoords;

        const fetchOsrmRoadRoute = async (orig, dest) => {
            try {
                const url = `https://router.project-osrm.org/route/v1/driving/${orig.lng},${orig.lat};${dest.lng},${dest.lat}?overview=full&geometries=geojson`;
                const resp = await fetch(url);
                const data = await resp.json();
                if (!isCancelled && data?.code === 'Ok' && data?.routes?.[0]?.geometry?.coordinates?.length > 0) {
                    const coords = data.routes[0].geometry.coordinates.map(([lng, lat]) => ({ lat, lng }));
                    setRoutePath(coords);
                    return true;
                }
            } catch (err) {
                console.warn("Error consultando ruta vial OSRM:", err);
            }
            if (!isCancelled) {
                setRoutePath([orig, dest]);
            }
            return false;
        };

        const hasValidDropoffCoords = Boolean(
            trip?.dropoff_lat &&
            trip?.dropoff_lng &&
            (Math.abs(arrivalCoords.lat - destinationCoords.lat) > 0.0005 || Math.abs(arrivalCoords.lng - destinationCoords.lng) > 0.0005)
        );

        if (window.google && window.google.maps && window.google.maps.DirectionsService) {
            const destinationQuery = hasValidDropoffCoords
                ? destinationCoords
                : (trip?.dropoff_address || destinationCoords);

            const directionsService = new window.google.maps.DirectionsService();
            directionsService.route(
                {
                    origin: originPt,
                    destination: destinationQuery,
                    travelMode: window.google.maps.TravelMode.DRIVING
                },
                (result, status) => {
                    if (isCancelled) return;
                    if (status === window.google.maps.DirectionsStatus.OK && result?.routes?.[0]?.overview_path?.length > 0) {
                        const overviewPath = result.routes[0].overview_path.map(p => ({
                            lat: p.lat(),
                            lng: p.lng()
                        }));
                        setRoutePath(overviewPath);

                        if (!hasValidDropoffCoords && result.routes[0].legs?.[0]?.end_location) {
                            const endLoc = result.routes[0].legs[0].end_location;
                            const newDest = getOffsetLatLng(endLoc.lat(), endLoc.lng(), 10);
                            if (Math.abs(newDest.lat - destinationCoords.lat) > 0.001 || Math.abs(newDest.lng - destinationCoords.lng) > 0.001) {
                                setDestinationCoords(newDest);
                            }
                        }
                    } else {
                        // Si Google Directions devuelve REQUEST_DENIED o falla, usar motor vial OSRM por carreteras
                        fetchOsrmRoadRoute(originPt, destinationCoords);
                    }
                }
            );
        } else {
            fetchOsrmRoadRoute(originPt, destinationCoords);
        }

        return () => {
            isCancelled = true;
        };
    }, [show, showDestination, arrivalCoords, departureCoords, destinationCoords, trip]);

    // Ajustar zoom y límites del mapa al cargar los marcadores y el caminito de la ruta
    useEffect(() => {
        if (map && arrivalCoords) {
            const bounds = new window.google.maps.LatLngBounds();
            bounds.extend(arrivalCoords);
            
            let hasMultiple = false;
            
            if (showDepartureTime && departureCoords) {
                bounds.extend(departureCoords);
                hasMultiple = true;
            }
            if (showDestination && destinationCoords) {
                bounds.extend(destinationCoords);
                hasMultiple = true;
                if (routePath && routePath.length > 0) {
                    routePath.forEach(pt => bounds.extend(pt));
                }
            }
            
            if (hasMultiple) {
                map.fitBounds(bounds);
                // Evitar zoom demasiado cercano
                const listener = window.google.maps.event.addListener(map, 'idle', () => {
                    if (map.getZoom() > 18) map.setZoom(17);
                    window.google.maps.event.removeListener(listener);
                });
            } else {
                map.setCenter(arrivalCoords);
                map.setZoom(17);
            }
        }
    }, [map, arrivalCoords, departureCoords, destinationCoords, showDepartureTime, showDestination, routePath]);

    if (!trip) return null;

    const handleDownloadPng = async () => {
        if (!reportRef.current) return;
        setIsDownloading(true);

        try {
            // Pequeña pausa para asegurar renderizado correcto
            await new Promise(resolve => setTimeout(resolve, 500));

            const canvas = await html2canvas(reportRef.current, {
                useCORS: true,
                allowTaint: false,
                scale: 2 // Mayor calidad de imagen
            });

            const link = document.createElement('a');
            link.download = `evidencia_gps_viaje_${trip.id}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (error) {
            console.error("Error al generar la imagen:", error);
            alert("No se pudo generar la imagen del reporte. Por favor intente nuevamente.");
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton className="bg-dark text-white border-0 py-3">
                <Modal.Title className="h5 fw-bold mb-0 d-flex align-items-center gap-2">
                    <ShieldCheck className="text-success" />
                    Generador de Evidencia GPS de Espera
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-light">
                <Row>
                    {/* Panel de Controles (Editable) */}
                    <Col md={4} className="border-end border-2">
                        <h6 className="fw-bold mb-3 text-secondary">Ajustar Parámetros de Evidencia</h6>
                        <Form>
                            <Form.Group className="mb-2">
                                <Form.Label className="small fw-semibold mb-1">Nombre Conductor</Form.Label>
                                <Form.Control 
                                    size="sm"
                                    type="text" 
                                    value={driverName} 
                                    onChange={(e) => setDriverName(e.target.value)} 
                                />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small fw-semibold mb-1">Código Móvil</Form.Label>
                                <Form.Control 
                                    size="sm"
                                    type="text" 
                                    value={vehicleCode} 
                                    onChange={(e) => setVehicleCode(e.target.value)} 
                                />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small fw-semibold mb-1">Fecha del Servicio</Form.Label>
                                <Form.Control 
                                    size="sm"
                                    type="date" 
                                    value={serviceDate} 
                                    onChange={(e) => setServiceDate(e.target.value)} 
                                />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small fw-semibold mb-1">Hora Llegada (Espera)</Form.Label>
                                <Form.Control 
                                    size="sm"
                                    type="time" 
                                    value={arrivalTime} 
                                    onChange={(e) => setArrivalTime(e.target.value)} 
                                />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Check 
                                    type="switch"
                                    id="show-departure-switch"
                                    label={<span className="small fw-semibold">Incluir Hora de Salida</span>}
                                    checked={showDepartureTime}
                                    onChange={(e) => setShowDepartureTime(e.target.checked)}
                                    className="mb-2"
                                />
                            </Form.Group>
                            {showDepartureTime && (
                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-semibold mb-1">Hora Salida (No Show / Retiro)</Form.Label>
                                    <Form.Control 
                                        size="sm"
                                        type="time" 
                                        value={departureTime} 
                                        onChange={(e) => setDepartureTime(e.target.value)} 
                                    />
                                </Form.Group>
                            )}
                            <Form.Group className="mb-2">
                                <Form.Check 
                                    type="switch"
                                    id="show-destination-switch"
                                    label={<span className="small fw-semibold">Incluir Destino y Ruta</span>}
                                    checked={showDestination}
                                    onChange={(e) => setShowDestination(e.target.checked)}
                                    className="mb-2"
                                />
                            </Form.Group>
                            {showDestination && (
                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-semibold mb-1">Hora Llegada Destino</Form.Label>
                                    <Form.Control 
                                        size="sm"
                                        type="time" 
                                        value={destinationTime} 
                                        onChange={(e) => setDestinationTime(e.target.value)} 
                                    />
                                </Form.Group>
                            )}
                        </Form>
                        <Button 
                            variant="success" 
                            className="w-100 fw-bold d-flex align-items-center justify-content-center gap-2"
                            onClick={handleDownloadPng}
                            disabled={isDownloading}
                        >
                            <Download size={18} />
                            {isDownloading ? 'Generando...' : 'Descargar Evidencia PNG'}
                        </Button>
                    </Col>

                    {/* Previsualización del Reporte (Este bloque se exportará como Imagen) */}
                    <Col md={8} className="d-flex justify-content-center align-items-center p-2">
                        <div 
                            ref={reportRef} 
                            style={{ 
                                width: '550px', 
                                backgroundColor: '#ffffff', 
                                padding: '24px', 
                                borderRadius: '8px', 
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                fontFamily: 'system-ui, -apple-system, sans-serif'
                            }}
                        >
                            {/* Encabezado */}
                            <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                                <div>
                                    <h4 className="fw-bold m-0" style={{ color: '#0d6efd', tracking: '-0.5px' }}>
                                        Emi<span style={{ color: '#ffc107' }}>Go</span>
                                    </h4>
                                    <span style={{ fontSize: '10px', color: '#6c757d', fontWeight: 'bold' }}>TRANSPORTE CORPORATIVO</span>
                                </div>
                                <div className="text-end">
                                    <span className="fw-bold d-block text-primary" style={{ fontSize: '12px' }}>CERTIFICADO DE POSICIONAMIENTO GPS</span>
                                    <span style={{ fontSize: '10px', color: '#6c757d' }}>Servicio #{trip.id}</span>
                                </div>
                            </div>

                            {/* Datos del Servicio */}
                            <Card className="border-0 bg-light p-3 mb-3" style={{ borderRadius: '6px' }}>
                                <Row className="g-2" style={{ fontSize: '11px' }}>
                                    <Col xs={6}>
                                        <div className="d-flex align-items-center gap-1 text-muted mb-1">
                                            <User size={12} />
                                            <span>Conductor:</span>
                                        </div>
                                        <strong className="text-dark d-block">{driverName}</strong>
                                    </Col>
                                    <Col xs={6}>
                                        <div className="d-flex align-items-center gap-1 text-muted mb-1">
                                            <Truck size={12} />
                                            <span>Móvil:</span>
                                        </div>
                                        <strong className="text-dark d-block">Móvil {vehicleCode}</strong>
                                    </Col>
                                    <Col xs={12}>
                                        <div className="d-flex align-items-center gap-1 text-muted mb-1 mt-1">
                                            <MapPin size={12} />
                                            <span>Dirección Origen:</span>
                                        </div>
                                        <span className="text-dark fw-semibold d-block">{trip.pickup_address}</span>
                                    </Col>
                                    {showDestination && trip.dropoff_address && (
                                        <Col xs={12}>
                                            <div className="d-flex align-items-center gap-1 text-muted mb-1 mt-1">
                                                <MapPin size={12} />
                                                <span>Dirección Destino:</span>
                                            </div>
                                            <span className="text-dark fw-semibold d-block">{trip.dropoff_address}</span>
                                        </Col>
                                    )}
                                    {trip.external_reference && (
                                        <Col xs={12}>
                                            <div className="d-flex align-items-center gap-1 text-muted mb-1 mt-1">
                                                <ShieldCheck size={12} />
                                                <span>Ref. Reserva Booking:</span>
                                            </div>
                                            <strong className="text-dark">{trip.external_reference}</strong>
                                        </Col>
                                    )}
                                </Row>
                            </Card>

                            {/* Mapa */}
                            <div className="mb-3 border rounded overflow-hidden">
                                {arrivalCoords && (
                                    <GoogleMap
                                        mapContainerStyle={mapContainerStyle}
                                        onLoad={setMap}
                                        options={{
                                            disableDefaultUI: true,
                                            gestureHandling: 'none',
                                            styles: [
                                                { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }
                                            ]
                                        }}
                                    >
                                        <MarkerF 
                                            position={arrivalCoords} 
                                            label={{ text: "A", color: "#ffffff", fontWeight: "bold" }}
                                            options={{ title: "Hora Llegada / Espera" }}
                                        />
                                        {showDepartureTime && departureCoords && (
                                            <MarkerF 
                                                position={departureCoords} 
                                                label={{ text: "S", color: "#ffffff", fontWeight: "bold" }}
                                                options={{ title: "Hora Salida / Retiro" }}
                                            />
                                        )}
                                        {showDestination && destinationCoords && (
                                            <>
                                                <MarkerF 
                                                    position={destinationCoords} 
                                                    label={{ text: "D", color: "#ffffff", fontWeight: "bold" }}
                                                    options={{ title: "Hora Llegada Destino" }}
                                                />
                                                {routePath && routePath.length > 0 && (
                                                    <PolylineF
                                                        key={`poly-${routePath.length}-${destinationCoords.lat.toFixed(5)}`}
                                                        path={routePath}
                                                        options={{
                                                            strokeColor: '#0d6efd',
                                                            strokeOpacity: 0.85,
                                                            strokeWeight: 4,
                                                            geodesic: false
                                                        }}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </GoogleMap>
                                )}
                            </div>

                            {/* Logs GPS */}
                            <Table size="sm" responsive className="align-middle border mb-2" style={{ fontSize: '10px' }}>
                                <thead className="bg-light">
                                    <tr>
                                        <th>Fecha / Hora Local</th>
                                        <th>Latitud</th>
                                        <th>Longitud</th>
                                        <th>Estado de Dispositivo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {arrivalCoords && (
                                        <tr>
                                            <td>
                                                <div className="d-flex align-items-center gap-1 font-monospace">
                                                    <Calendar size={10} className="text-muted" />
                                                    <span>{serviceDate.split('-').reverse().join('-')}</span>
                                                    <Clock size={10} className="text-muted ms-1" />
                                                    <span>{arrivalTime}</span>
                                                </div>
                                            </td>
                                            <td className="font-monospace text-muted">{arrivalCoords.lat.toFixed(6)}</td>
                                            <td className="font-monospace text-muted">{arrivalCoords.lng.toFixed(6)}</td>
                                            <td><Badge bg="success" style={{ fontSize: '8px' }}>En Origen (Espera)</Badge></td>
                                        </tr>
                                    )}
                                    {showDepartureTime && departureCoords && (
                                        <tr>
                                            <td>
                                                <div className="d-flex align-items-center gap-1 font-monospace">
                                                    <Calendar size={10} className="text-muted" />
                                                    <span>{serviceDate.split('-').reverse().join('-')}</span>
                                                    <Clock size={10} className="text-muted ms-1" />
                                                    <span>{departureTime}</span>
                                                </div>
                                            </td>
                                            <td className="font-monospace text-muted">{departureCoords.lat.toFixed(6)}</td>
                                            <td className="font-monospace text-muted">{departureCoords.lng.toFixed(6)}</td>
                                            <td><Badge bg="danger" style={{ fontSize: '8px' }}>Retiro / Salida</Badge></td>
                                        </tr>
                                    )}
                                    {showDestination && destinationCoords && (
                                        <tr>
                                            <td>
                                                <div className="d-flex align-items-center gap-1 font-monospace">
                                                    <Calendar size={10} className="text-muted" />
                                                    <span>{serviceDate.split('-').reverse().join('-')}</span>
                                                    <Clock size={10} className="text-muted ms-1" />
                                                    <span>{destinationTime}</span>
                                                </div>
                                            </td>
                                            <td className="font-monospace text-muted">{destinationCoords.lat.toFixed(6)}</td>
                                            <td className="font-monospace text-muted">{destinationCoords.lng.toFixed(6)}</td>
                                            <td><Badge bg="primary" style={{ fontSize: '8px' }}>Llegada Destino</Badge></td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>

                            {/* Nota de pie */}
                            <div className="text-center text-muted" style={{ fontSize: '8px', lineHeight: '1.2' }}>
                                🛡️ Reporte generado automáticamente por el Sistema de Monitoreo Satelital EmiGo.
                                <br />
                                Las coordenadas corresponden al posicionamiento registrado por el dispositivo del conductor asignado.
                            </div>
                        </div>
                    </Col>
                </Row>
            </Modal.Body>
        </Modal>
    );
};

export default GPSEvidenceGeneratorModal;
